import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "../ui";
import { WOERTERBUECHER, aktuelleSprache } from "../../lib/texte";

interface Props {
  children: ReactNode;
  /** Ersetzt den Standardsatz — die Karte sagt etwas anderes als eine Liste. */
  titel?: string;
  hinweis?: string;
  /** Zusätzlicher Knopf neben „Neu laden" und „Noch einmal". */
  aktion?: ReactNode;
}
interface State {
  fehler: Error | null;
}

/**
 * Der Auffangkorb. Ohne ihn nimmt ein einziger Fehler beim Zeichnen den ganzen
 * Baum mit und hinterlässt eine weiße Seite — ohne jeden Hinweis, was zu tun
 * wäre. In App.tsx liegt er ÜBER dem Router-Outlet, aber UNTER der
 * Kopfleiste: stürzt ein Bildschirm ab, soll man wegnavigieren können, statt
 * neu laden zu müssen.
 *
 * Muss eine Klasse sein: `componentDidCatch` hat in Funktionskomponenten bis
 * heute keine Entsprechung. Deshalb kommen die Sätze auch NICHT aus `useT()`
 * (ein Hook geht in einer Klasse nicht), sondern über `aktuelleSprache()` aus
 * demselben Wörterbuch — ein zweiter Satz wäre der, der beim nächsten Mal
 * vergessen wird.
 */
export class ScreenBoundary extends Component<Props, State> {
  override state: State = { fehler: null };

  static getDerivedStateFromError(fehler: Error): State {
    return { fehler };
  }

  override componentDidCatch(fehler: Error, info: ErrorInfo): void {
    // In die Konsole, nicht zu einem Fremdanbieter: es gibt hier keinen
    // Fehlerdienst, und im Wortlaut könnten Projektdaten stehen.
    console.error("[LILASp Projekte] Bildschirm abgestürzt:", fehler, info.componentStack);
  }

  override render() {
    if (!this.state.fehler) return this.props.children;
    const t = WOERTERBUECHER[aktuelleSprache()];
    return (
      <div className="huelle huelle-schmal" role="alert">
        <div className="card" style={{ padding: 28, textAlign: "center" }}>
          <h1 className="seitentitel">{this.props.titel ?? t.fehler.umgefallen}</h1>
          <p className="unterzeile" style={{ marginTop: 6 }}>{this.props.hinweis ?? t.fehler.umgefallenHinweis}</p>
          <p className="font-mono" style={{ fontSize: 12, color: "var(--text-mute)", wordBreak: "break-word", margin: "14px 0 20px" }}>
            {this.state.fehler.message}
          </p>
          <div className="reihe wrap" style={{ justifyContent: "center" }}>
            <Button type="button" icon="arrow-clockwise" onClick={() => window.location.reload()}>
              {t.fehler.neuLaden}
            </Button>
            <Button type="button" variant="ghost" onClick={() => this.setState({ fehler: null })}>
              {t.fehler.nochmal}
            </Button>
            {this.props.aktion}
          </div>
        </div>
      </div>
    );
  }
}
